import React, { useState } from "react";
import Layout from "../../../components/Layout";
import Container from "../../../components/Container";
import { useHistory } from "react-router-dom";
import Flex from "../../../components/Flex";
import { useAuthState } from "../../../components/AuthProvider";
import Content from "../../../components/Content";
import ButtonList from "../../../components/ButtonList";
import LinkButton from "../../../components/LinkButton";
import DataTable from "../../../components/DataTable";
import DefaultBreadcrumbs from "../../../components/DefaultBreadcrumbs";
import _groupBy from "lodash/groupBy";
import Spinner from "../../../components/Loader/Spinner";

import styles from "./Organizations.module.scss";

const Organizations = () => {
  const clientConfigApiDomain = process.env.REACT_APP_CLIENT_CONFIG_API_DOMAIN;
  const orgApiUrl = `${clientConfigApiDomain}/organizations`;

  const history = useHistory();
  const { user } = useAuthState();

  const [orgData, setOrgData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const SinceCell = ({
    cell: {
      value,
      row: { original: selectedSource }
    }
  }) => {
    const oneDay = 1000 * 60 * 60 * 24;
    return (
      <span>
        {((new Date() - new Date(selectedSource.created_at)) / oneDay).toFixed(
          0
        )}
      </span>
    );
  };

  const clickableCell = ({
    cell: {
      value,
      row: { original: selectedOrganization }
    }
  }) => {
    const onClick = () => {
      history.push({
        pathname: `/admin/organizations/${selectedOrganization.id}`
      });
    };

    return (
      <span className={styles.cell} onClick={onClick}>
        {value}
      </span>
    );
  };

  React.useEffect(() => {
    window.scrollTo(0, 0);
    const createUserClientsOrganizations = () => {
      const userClientMapped = user.clients.map(client => ({
        client: client,
        organizationName: client.organization.name,
        organization: client.organization
      }));
      const userClientGroupByOrg = _groupBy(
        userClientMapped,
        `organizationName`
      );
      const userClient = Object.keys(userClientGroupByOrg).map(org => ({
        ...userClientGroupByOrg[org][0].organization,
        clients: userClientGroupByOrg[org].map(
          childValues => childValues.client
        )
      }));
      setOrgData(userClient);
    };

    const fetchOrganizations = async () => {
      const response = await fetch(`${orgApiUrl}/`);
      const data = await response.json();

      if (data.hasOwnProperty("results")) {
        setOrgData(data.results);
      }
    };

    if (!hasLoaded) {
      setIsLoading(true);
      if (user.role === "Global Admin") {
        fetchOrganizations();
      } else {
        createUserClientsOrganizations();
      }
      setIsLoading(false);
      setHasLoaded(true);
    }
  }, [hasLoaded, orgApiUrl, user.clients, user.role]);

  const actionCell = ({
    cell: {
      value,
      row: { original: selectedOrg }
    }
  }) => {
    const onDetailsClick = () => {
      history.push(`/admin/organizations/${selectedOrg.id}`);
    };

    const onEditClick = () => {
      history.push(`/admin/organizations/${selectedOrg.id}/edit`);
    };

    const isEditVisible = selectedOrg => {
      return (
        user.role === "Global Admin" || user.orgIds.includes(selectedOrg.id)
      );
    };

    return (
      <ButtonList className={styles.icons}>
        <LinkButton icon="view" onClick={onDetailsClick}>
          Details
        </LinkButton>
        {isEditVisible(selectedOrg) && (
          <LinkButton icon="edit" onClick={onEditClick}>
            Edit
          </LinkButton>
        )}
      </ButtonList>
    );
  };

  const clientCountCell = ({
    cell: {
      value,
      row: { original: selectedOrg }
    }
  }) => {
    if (selectedOrg && selectedOrg.clients && selectedOrg.clients.length > 0) {
      const clientNames = selectedOrg.clients.map((client, i) => client.name);
      return (
        <>
          {/* Only considering the first 3 to be shown on the screen 
          in case there are a lot of accounts to prevent bloating */}
          {clientNames.slice(0, 3).join(", ")}{" "}
          {selectedOrg.clients.length > 3 ? ", ... " : null}
          <i>({selectedOrg.clients.length} Accounts)</i>
        </>
      );
    } else {
      return "No Accounts";
    }
  };

  const orgActiveCountCell = ({ cell, row: { original: selectedOrg } }) => {
    if (selectedOrg && selectedOrg.clients && selectedOrg.clients.length > 0) {
      const countType =
        cell.column.Header === "Active Lives"
          ? "active_lives"
          : "active_records";

      let counts = 0;
      selectedOrg.clients.forEach(client => {
        counts += client[countType];
      });
      return (
        <>
          <span>{counts}</span>
        </>
      );
    } else {
      return "-";
    }
  };

  const columns = [
    {
      Header: "Organization",
      accessor: "name",
      Cell: clickableCell
    },
    {
      Header: "Account(s)",
      accessor: "clients",
      Cell: clientCountCell
    },
    {
      Header: "Active Lives",
      Cell: orgActiveCountCell
    },
    {
      Header: "Active Records",
      Cell: orgActiveCountCell
    },
    {
      Header: "Last Edited (days)",
      accessor: "created_at",
      Cell: SinceCell
    },
    {
      Header: "",
      accessor: "actions",
      disableFilters: true,
      disableSortBy: true,
      Cell: actionCell
    }
  ];

  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          <Flex.Item span={12}>
            <DefaultBreadcrumbs />
          </Flex.Item>
          <Flex.Item span={12}>
            <Content>
              <h1>Organizations</h1>
            </Content>
            <div className={styles.base}>
              <ButtonList spacing={2} style={{ margin: "1rem 0" }}>
                {user.role === "Global Admin" ? (
                  <LinkButton
                    icon="add"
                    onClick={() => {
                      history.push("/admin/organizations/add");
                    }}
                  >
                    Add Organization
                  </LinkButton>
                ) : null}
              </ButtonList>
              {isLoading ? (
                <Spinner />
              ) : hasLoaded ? (
                <DataTable
                  columns={columns}
                  data={orgData}
                  hasSorting
                  hasPagination
                />
              ) : null}
            </div>
          </Flex.Item>
        </Flex>
      </Container>
    </Layout>
  );
};

export default Organizations;
